import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/services/api/client.gen";
import { useAuth } from "@/contexts/AuthContext";
import {
  DIMENSION_SLUGS,
  DATE_FILTERS,
  SORT_BY,
  SEARCH_MODES,
  SEARCH_API_PATHS,
} from "@/constants/search";
import type {
  SearchFilterDimension,
  SearchFilterDefaults,
  SearchFiltersResponse,
} from "@/types/search";

/** Fallback defaults used before the filter config loads */
const DEFAULT_FALLBACKS: SearchFilterDefaults = {
  attributes: ["active"],
  entityTypes: ["project", "workflow"],
  dateFilter: DATE_FILTERS.ALL_TIME,
  sortBy: SORT_BY.UPDATED_AT,
  searchMode: SEARCH_MODES.SMART,
};

/**
 * Extracts default slugs for a given dimension from the filter config.
 * Returns slugs of all values where isDefault is true.
 */
export function extractDefaults(
  dimensions: SearchFilterDimension[],
  dimensionSlug: string,
): string[] {
  const dimension = dimensions.find((d) => d.slug === dimensionSlug);
  if (!dimension) return [];
  return dimension.values.filter((v) => v.isDefault).map((v) => v.slug);
}

/**
 * Fetches search filter configuration from GET /v1/search/filters.
 * Computes default selections from the isDefault flags per dimension.
 *
 * Returns:
 * - filters: array of SearchFilterDimension (sorted by sortOrder)
 * - defaults: computed default values per dimension
 * - isLoading: whether the query is still loading
 * - error: query error, if any
 */
export function useSearchFilters() {
  const { user } = useAuth();

  const query = useQuery<SearchFilterDimension[]>({
    queryKey: ["searchFilters"],
    queryFn: async () => {
      const response = await client.get<SearchFiltersResponse>({
        url: SEARCH_API_PATHS.FILTERS,
        security: [{ scheme: "bearer", type: "http" }],
      });
      const result = response.data as SearchFiltersResponse | undefined;
      return result?.data ?? [];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes — matches Cache-Control from API
  });

  const defaults = useMemo((): SearchFilterDefaults => {
    const data = query.data;
    if (!data || data.length === 0) return DEFAULT_FALLBACKS;

    return {
      attributes: extractDefaults(data, DIMENSION_SLUGS.STATUS_ATTRIBUTE),
      entityTypes: extractDefaults(data, DIMENSION_SLUGS.ENTITY_TYPE),
      dateFilter:
        extractDefaults(data, DIMENSION_SLUGS.DATE_RANGE)[0] ??
        DATE_FILTERS.ALL_TIME,
      sortBy:
        extractDefaults(data, DIMENSION_SLUGS.SORT)[0] ?? SORT_BY.UPDATED_AT,
      searchMode:
        extractDefaults(data, DIMENSION_SLUGS.SEARCH_MODE)[0] ??
        SEARCH_MODES.SMART,
    };
  }, [query.data]);

  return {
    filters: query.data ?? [],
    defaults,
    isLoading: query.isLoading,
    error: query.error,
  };
}
